import React, { useEffect, useRef } from "react";
// COMPONENTS
import Header from "Components/Header/Header";
import MobileHeader from "Components/Header/MobileHeader";
import SiteFooter from "./SiteFooter";
// STYLES
import { SitePageWrapper } from "./WebsiteTheme.style";
import "Styles/typography.css";

/**
 * Scroll reveal: marks each [data-reveal] element as revealed the first time it enters the screen.
 * Motion is only switched on (data-motion="on") once the observer is running, so the page never
 * stays hidden; visitors who prefer reduced motion, or browsers without IntersectionObserver,
 * get everything shown straight away.
 */
export const useScrollReveal = (rootRef) => {
	useEffect(() => {
		const root = rootRef.current;
		if (!root) return undefined;

		const items = Array.from(root.querySelectorAll("[data-reveal]"));
		const reduceMotion = window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches;

		if (reduceMotion || !("IntersectionObserver" in window)) {
			items.forEach((item) => item.setAttribute("data-revealed", "true"));
			return undefined;
		}

		const observer = new IntersectionObserver(
			(entries) => {
				entries.forEach((entry) => {
					if (entry.isIntersecting) {
						entry.target.setAttribute("data-revealed", "true");
						observer.unobserve(entry.target);
					}
				});
			},
			{ rootMargin: "0px 0px -8% 0px", threshold: 0.12 }
		);

		items.forEach((item) => observer.observe(item));
		root.setAttribute("data-motion", "on");

		return () => observer.disconnect();
	}, [rootRef]);
};

/**
 * Page frame for the Home and About pages: the shared site header and mobile menu (same menu as every
 * other page, original logo), the page sections inside the shared theme wrapper, and the shared footer
 * in the page palette. The footer's own demo band is hidden because each page ends with SiteCtaBand.
 */
const SitePageShell = ({ className = "", children }) => {
	const pageRef = useRef(null);
	useScrollReveal(pageRef);

	return (
		<>
			{/* Same header as every other website page (original logo) */}
			<Header />
			<MobileHeader />

			<SitePageWrapper ref={pageRef} className={className}>
				<main>{children}</main>
			</SitePageWrapper>

			<SiteFooter isCTAHidden={true} />
		</>
	);
};

export default SitePageShell;
